import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';

import 'highcharts/modules/accessibility';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/no-data-to-display';

import HighchartsReact from 'highcharts-react-official';
import merge from 'lodash/merge';

import { getHighchartsOptions, updateChartColors } from './utilities/helpers';

import ExportMenu from './components/ExportMenu';
import Loader from '../../components/Loader';

import styles from './_index.module.scss';

// For some reason this does not take effect when included in `getHighchartsOptions`, it has to be set globally.
// Fallback to `B` for Billion instead of `G` for Giga. Possible FIXME is to use numbro decimal formatter `formatData` to handle labels.
Highcharts.setOptions({
  lang: {
    numericSymbols: ['k', 'M', 'B', 'T', 'P', 'E'],
  },
});

const Chart = ({
  chartTitle,
  className,
  exportMenuOptions,
  exportName,
  handleChartRenderEvent,
  hasColorMap,
  hasExportButtonText,
  hasLegend,
  headerActions,
  isUpdating,
  maxHeight,
  onChartCallback,
  options,
  prescriptText,
  setModifiedData,
  showExportMenu = true,
}) => {
  const [seriesColorMap, setSeriesColorMap] = useState({});
  const chartRef = useRef();
  const textElementsRef = useRef([]);

  const chartOptions = getHighchartsOptions({
    chartTitle,
    exportName,
    hasLegend,
    maxHeight,
    setModifiedData,
    ...options,
  });

  const renderChart = ({ customOptions = {}, isExport = false, ref }) => {
    const mergedOptions = merge({}, chartOptions, customOptions);

    const updatedSeries =
      !isExport && hasColorMap
        ? mergedOptions.series.map((series) => ({
            ...series,
            color: seriesColorMap[series.name],
          }))
        : mergedOptions?.series;

    if (setModifiedData) {
      setModifiedData({ ...mergedOptions, series: updatedSeries });
    }

    if (!isExport && chartRef.current) {
      chartRef?.current?.chart?.update(
        {
          chart: {
            events: {
              render: () =>
                handleChartRenderEvent({
                  chart: chartRef.current.chart,
                  textElements: textElementsRef.current,
                }),
            },
          },
          series: updatedSeries,
        },
        true,
        true
      );
    }

    return (
      <HighchartsReact
        highcharts={Highcharts}
        options={{ ...mergedOptions, series: updatedSeries }}
        ref={ref}
      />
    );
  };

  useEffect(() => {
    if (hasColorMap) {
      const newColorMap = updateChartColors({
        seriesColorMap,
        seriesData: chartOptions.series || [],
      });

      if (Object.keys(newColorMap).some((key) => newColorMap[key] !== seriesColorMap[key])) {
        setSeriesColorMap(newColorMap);
      }
    }
  }, [chartOptions.series, seriesColorMap]);

  useEffect(() => {
    if (chartRef?.current) {
      onChartCallback(chartRef.current.chart);
    }
  }, [isUpdating, chartRef.current]);

  return (
    <div className={classNames(styles['chart'], className)}>
      <div>
        <div>
          <h4>{chartTitle}</h4>
          {prescriptText}
        </div>
        <div className={styles['chart-actions']}>
          {headerActions}
          {showExportMenu && (
            <ExportMenu
              chartOptions={chartOptions}
              chartRef={chartRef}
              hasExportButtonText={hasExportButtonText}
              options={exportMenuOptions}
              renderChart={renderChart}
            />
          )}
        </div>
      </div>
      <div>{renderChart({ ref: chartRef })}</div>
      {isUpdating && (
        <div className={styles['chart-loader']}>
          <Loader />
        </div>
      )}
    </div>
  );
};

Chart.defaultProps = {
  handleChartRenderEvent: () => {},
  hasColorMap: false,
  onChartCallback: () => {},
};

Chart.propTypes = {
  chartTitle: PropTypes.string,
  className: PropTypes.string,
  exportMenuOptions: PropTypes.arrayOf(PropTypes.object),
  exportName: PropTypes.string,
  handleChartRenderEvent: PropTypes.func,
  hasColorMap: PropTypes.bool,
  hasExportButtonText: PropTypes.bool,
  hasLegend: PropTypes.bool,
  headerActions: PropTypes.node,
  isUpdating: PropTypes.bool,
  maxHeight: PropTypes.number,
  onChartCallback: PropTypes.func,
  options: PropTypes.object,
  prescriptText: PropTypes.node,
  setModifiedData: PropTypes.func,
  showExportMenu: PropTypes.bool,
};

export default Chart;
